import React, { useState } from 'react';
import { transformExtent } from 'ol/proj';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Stack,
  TextField,
  List,
  ListItem,
  Typography,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import { 
  LocationOn, 
  Search, 
  Clear,
  Place,
  MyLocation
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

import { themeColor, innerColor, modernDesignSystem } from '../constants/themes';

const SearchPositionUtil = () => {
  const { map } = useSelector((state) => state.globalMaps[0]);

  const [query, setQuery] = useState('');
  const [locations, setLocations] = useState(null);
  const [progressVisible, setProgressVisible] = useState('none');
  const [activeLocation, setActiveLocation] = useState(null);

  const handleSearch = () => {
    if (query.length < 3) {
      toast.error('Lütfen daha uzun bir konum adı giriniz!', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
      setQuery('');
      return;
    }
    setProgressVisible('flex');
    geocode(query);
  };

  const geocode = async (query) => {
    try {
      const { data } = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: query,
            format: 'geojson',
            addressdetails: 1,
            limit: 5,
          },
        }
      );

      if (data.features.length > 0) {
        const { features } = data;
        setLocations(features);
        toast.success('Aradığınız kriterlere uygun konumlar listelendi', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
        });
        setProgressVisible('none');
      } else {
        toast.error('Sonuç bulunamadı!', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
        });
        setProgressVisible('none');
      }
    } catch (error) {
      toast.error('Sorgulama servisinde hata oluştu!', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
      setProgressVisible('none');
    }
  };

  const zoomToResult = (e) => {
    // Event bubbling problemi için closest kullanarak doğru elementi bul
    const targetElement = e.currentTarget;
    const placeId = targetElement.id;
    
    const selectedLocation = locations.find(
      (location) => location.properties.place_id == placeId
    );
    
    if (!selectedLocation) {
      console.error('Seçilen konum bulunamadı:', placeId);
      return;
    }
    
    setActiveLocation(selectedLocation);
    
    // bbox kontrolü ile güvenli erişim
    const bbox = selectedLocation.bbox;
    if (!bbox || !Array.isArray(bbox) || bbox.length !== 4) {
      console.error('Bbox bilgisi eksik veya hatalı:', selectedLocation);
      toast.error('Bu konum için harita bilgisi eksik!', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      });
      return;
    }
    
    const targetProjection = map.getView().getProjection().getCode();
    const bboxEPSG32637 = transformExtent(bbox, 'EPSG:4326', targetProjection);

    map.getView().fit(bboxEPSG32637, { padding: [10, 10, 10, 10] });
  };

  return (
    <Box sx={{ padding: modernDesignSystem.spacing.xs }}>
      {/* Kompakt Başlık Bölümü */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${modernDesignSystem.colors.secondary.surface} 0%, ${modernDesignSystem.colors.neutral.gray50} 100%)`,
          padding: modernDesignSystem.spacing.sm,
          borderRadius: modernDesignSystem.borders.radius.small,
          marginBottom: modernDesignSystem.spacing.sm,
          border: `1px solid ${modernDesignSystem.colors.secondary.light}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: 0.5 }}>
          <Place sx={{ color: modernDesignSystem.colors.secondary.main, fontSize: '1.1rem' }} />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: modernDesignSystem.colors.secondary.dark,
              fontSize: modernDesignSystem.typography.fontSize.base,
            }}
          >
            Adres Arama
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: modernDesignSystem.colors.neutral.gray600,
            fontSize: modernDesignSystem.typography.fontSize.xs,
          }}
        >
          Sokak, cadde, mahalle adı ile arama yapabilirsiniz
        </Typography>
      </Paper>

      <Stack spacing={modernDesignSystem.spacing.sm}>
        {/* Arama Kutusu */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Konum adı yazınız..."
            variant="outlined"
            size="small"
            fullWidth
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            sx={{
              backgroundColor: modernDesignSystem.colors.neutral.white,
              '& .MuiOutlinedInput-root': {
                borderRadius: modernDesignSystem.borders.radius.small,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: modernDesignSystem.colors.secondary.light,
                borderWidth: '2px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: modernDesignSystem.colors.secondary.main,
              },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: modernDesignSystem.colors.secondary.dark,
              }
            }}
          />
          {query && (
            <IconButton
              onClick={() => {
                setQuery('');
                setLocations(null);
                setActiveLocation(null);
              }}
              size="small"
              sx={{
                backgroundColor: modernDesignSystem.colors.neutral.gray200,
                '&:hover': {
                  backgroundColor: modernDesignSystem.colors.neutral.gray300,
                }
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Arama Butonu */}
        <Button
          onClick={handleSearch}
          variant="contained"
          fullWidth
          disabled={query.length < 3}
          startIcon={<Search />}
          size="small"
          sx={{
            background: `linear-gradient(135deg, ${modernDesignSystem.colors.secondary.main} 0%, ${modernDesignSystem.colors.secondary.dark} 100%)`,
            color: modernDesignSystem.colors.neutral.white,
            borderRadius: modernDesignSystem.borders.radius.small,
            padding: `${modernDesignSystem.spacing.sm} ${modernDesignSystem.spacing.md}`,
            fontSize: modernDesignSystem.typography.fontSize.sm,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: modernDesignSystem.shadows.medium,
            
            '&:hover:not(:disabled)': {
              background: `linear-gradient(135deg, ${modernDesignSystem.colors.secondary.dark} 0%, ${modernDesignSystem.colors.primary.dark} 100%)`,
              boxShadow: modernDesignSystem.shadows.elevated,
              transform: 'translateY(-1px)',
            },
            
            '&:disabled': {
              background: modernDesignSystem.colors.neutral.gray300,
              color: modernDesignSystem.colors.neutral.gray500,
              boxShadow: 'none',
            }
          }}
        >
          Konum Ara
        </Button>

        {/* Yükleme Göstergesi */}
        {progressVisible === 'flex' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: modernDesignSystem.spacing.lg,
            }}
          >
            <CircularProgress 
              size={30} 
              sx={{ 
                color: modernDesignSystem.colors.secondary.main 
              }} 
            />
          </Box>
        )}

        {/* Sonuçlar */}
        {locations && (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: modernDesignSystem.colors.neutral.gray50,
              borderRadius: modernDesignSystem.borders.radius.small,
              border: `1px solid ${modernDesignSystem.colors.neutral.gray200}`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                padding: modernDesignSystem.spacing.sm,
                background: `linear-gradient(135deg, ${modernDesignSystem.colors.secondary.surface} 0%, ${modernDesignSystem.colors.neutral.white} 100%)`,
                borderBottom: `1px solid ${modernDesignSystem.colors.neutral.gray200}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: modernDesignSystem.colors.secondary.dark,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <MyLocation fontSize="small" />
                {locations.length} Sonuç Bulundu
              </Typography>
            </Box>

            <List sx={{ padding: 0, maxHeight: '200px', overflow: 'auto' }}>
              {locations.map((location, index) => (
                <ListItem
                  key={index}
                  id={location.properties.place_id}
                  onClick={zoomToResult}
                  sx={{
                    borderBottom: index < locations.length - 1 ? `1px solid ${modernDesignSystem.colors.neutral.gray200}` : 'none',
                    backgroundColor:
                      activeLocation &&
                      activeLocation.properties.place_id === location.properties.place_id
                        ? modernDesignSystem.colors.secondary.surface
                        : modernDesignSystem.colors.neutral.white,
                    cursor: 'pointer',
                    padding: modernDesignSystem.spacing.sm,
                    transition: 'all 0.2s ease',
                    
                    '&:hover': {
                      backgroundColor: modernDesignSystem.colors.secondary.surface,
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                    <LocationOn 
                      fontSize="small" 
                      sx={{ 
                        color: modernDesignSystem.colors.secondary.main,
                        marginTop: '2px',
                        flexShrink: 0,
                      }} 
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: modernDesignSystem.typography.fontSize.xs,
                          color: modernDesignSystem.colors.neutral.gray800,
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {location.properties.display_name}
                      </Typography>
                      {activeLocation &&
                        activeLocation.properties.place_id === location.properties.place_id && (
                        <Chip
                          label="Seçili"
                          size="small"
                          sx={{
                            backgroundColor: modernDesignSystem.colors.secondary.main,
                            color: modernDesignSystem.colors.neutral.white,
                            height: '20px',
                            fontSize: '0.6rem',
                            marginTop: 0.5,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default SearchPositionUtil;
